import './ClientRegistrationPhysicalActivityQuestionnaireComponent.scss';
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import React, {useCallback, useEffect, useState} from "react";
import FormikRadioButtonGroupComponent
    from "../../../shared/components/form-controls/formik-radio-button-group/FormikRadioButtonGroupComponent";
import {CommonService} from "../../../shared/services";
import FormikTextAreaComponent from "../../../shared/components/form-controls/formik-text-area/FormikTextAreaComponent";
import HorizontalLineComponent
    from "../../../shared/components/horizontal-line/horizontal-line/HorizontalLineComponent";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import {ImageConfig, Misc, Patterns} from "../../../constants";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {IAPIResponseType} from "../../../shared/models/api.model";
import CheckBoxComponent from "../../../shared/components/form-controls/check-box/CheckBoxComponent";
import * as Yup from 'yup';
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikSelectDropdownComponent
    from "../../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";
import _ from "lodash";
import FormikCheckBoxComponent from "../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import FormikDatePickerComponent
    from "../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import FormControlLabelComponent from "../../../shared/components/form-control-label/FormControlLabelComponent";
import ErrorComponent from "../../../shared/components/error/ErrorComponent";
import ESignApprovalComponent from "../../../shared/components/e-sign-approval/ESignApprovalComponent";
import LinkComponent from "../../../shared/components/link/LinkComponent";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import SignaturePopupComponent from "../../../shared/components/signature-popup/SignaturePopupComponent";
import {getClientSignatureDetails} from "../../../store/actions/client.action";

interface ClientRegistrationPhysicalActivityQuestionnaireProps {
    onNext?: () => void;
    appointmentId: any
}

const InitialValues = {
    appointment_id: '',
    height: {
        value: "",
        unit: "ft-in"
    },
    weight: {
        value: "",
        unit: "kg"
    },
    current_activity_level: {
        frequency: "",
        activities: [],
        intensity: "",
        heart_rate_impact: "",
        exercise_capacity: "",
        additional_notes: "",
    },
    fitness_goals: {
        goals: "",
        past_exercise_experience: "",
        past_difficulty: {
            value: "",
            text: ""
        },
        trained_with_coach: {
            value: "",
            text: ""
        },
    },
    health_and_fitness: {
        under_medical_treatment: {
            value: "",
            text: ""
        },
        medical_conditions: {
            value: "",
            text: ""
        },
        doctor_advised_against_exercise: {
            value: "",
            text: ""
        },
    },
    additional_comments: "",
    consent: {
        client_name: "",
        date: new Date(),
        agreed_to_terms: false,
        is_authorized_representative: false,
        representative_name: "",
        signature: "",
        is_certification_and_consent_form_filled: true,
    }
}

const validationSchema = Yup.object().shape({
    height: Yup.object().shape({
        value: Yup.string()
            .required("Height is required"),
        unit: Yup.string()
            .oneOf(["ft-in", "cm"], "Invalid unit")
            .required("Height unit is required")
    }),
    weight: Yup.object().shape({
        value: Yup.string()
            .required("Weight is required"),
        unit: Yup.string()
            .oneOf(["kg", "lbs"], "Invalid unit")
            .required("Weight unit is required")
    }),
    current_activity_level: Yup.object().shape({
        frequency: Yup.string()
            .required("Activity frequency is required"),
        activities: Yup.array()
            .min(1, "At least one activity is required"),
        intensity: Yup.string()
            .required("Intensity level is required"),
        heart_rate_impact: Yup.string()
            .required("Heart rate impact is required"),
        exercise_capacity: Yup.string()
            .required("Exercise capacity is required"),
        additional_notes: Yup.string(),
    }),
    fitness_goals: Yup.object().shape({
        goals: Yup.string()
            .required("Fitness goals are required"),
        past_exercise_experience: Yup.string()
            .required("Past exercise experience is required"),
        past_difficulty: Yup.object({
            value: Yup.boolean().required(),
            text: Yup.string().when("value", {
                is: (value: boolean) => value,
                then: Yup.string().required('Information is required'),
                otherwise: Yup.string().nullable(),
            }),
        }),
        trained_with_coach: Yup.object({
            value: Yup.boolean().required(),
            text: Yup.string().when("value", {
                is: (value: boolean) => value,
                then: Yup.string().required('Information is required'),
                otherwise: Yup.string().nullable(),
            }),
        }),
    }),
    health_and_fitness: Yup.object().shape({
        under_medical_treatment: Yup.object({
            value: Yup.boolean().required(),
            text: Yup.string().when("value", {
                is: (value: boolean) => value,
                then: Yup.string().required('Information is required'),
                otherwise: Yup.string().nullable(),
            }),
        }),
        medical_conditions: Yup.object({
            value: Yup.boolean().required(),
            text: Yup.string().when("value", {
                is: (value: boolean) => value,
                then: Yup.string().required('Information is required'),
                otherwise: Yup.string().nullable(),
            }),
        }),
        doctor_advised_against_exercise: Yup.object({
            value: Yup.boolean().required(),
            text: Yup.string().when("value", {
                is: (value: boolean) => value,
                then: Yup.string().required('Information is required'),
                otherwise: Yup.string().nullable(),
            }),
        }),
    }),
    additional_comments: Yup.string(),
    consent: Yup.object().shape({
        is_authorized_representative: Yup.boolean(),
        representative_name: Yup.string().when("is_authorized_representative", {
            is: true,
            then: Yup.string().required('Authorized Representative Name is required')
        }),
        signature: Yup.string().required("Signature is required"),
        client_name: Yup.string().required('Name is required'),
        date: Yup.string().nullable(),
        agreed_to_terms: Yup.boolean().oneOf([true], 'Please acknowledge to the consent'),
    })
});


const ClientRegistrationPhysicalActivityQuestionnaireComponent = (props: ClientRegistrationPhysicalActivityQuestionnaireProps) => {
    const dispatch = useDispatch();
    const formikRef = React.useRef<any>(null);
    const {onNext, appointmentId} = props;
    const [initialValues, setInitialValues] = useState<any>(InitialValues);
    const [isLoading, setIsLoading] = useState(false);
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [getPhysicalActivityActivityData, setGetPhysicalActivityActivityData] = useState<any>(initialValues);
    const {clientSignatureDetails} = useSelector((state: IRootReducerState) => state.client);
    const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
    const {
        weightUnitsList,
        heightUnitsList,
        physicalActivityTypesList,
        physicalActivityFrequencyList,
        physicalActivityIntensityList,
        physicalActivityFrequencyForHeartRateImpactList,
        physicalActivityExerciseCapacityList
    } = useSelector((state: IRootReducerState) => state.staticData);
    const midIndex = Math.ceil(physicalActivityTypesList.length / 2);
    const column1 = physicalActivityTypesList.slice(0, midIndex);
    const column2 = physicalActivityTypesList.slice(midIndex);


    useEffect(() => {
        dispatch(getClientSignatureDetails(true, appointmentId));
    }, [dispatch, appointmentId]);

    //
    // useEffect(() => {
    //     if (currentUser) {
    //         setInitialValues((prevValues: any) => {
    //             const newInitialValues = {
    //                 ...prevValues, // Preserve other form values
    //                 consent: {
    //                     ...prevValues?.consent, // Ensure consent structure is maintained
    //                     client_name: `${currentUser.first_name} ${currentUser.last_name}`,
    //                 }
    //             };
    //
    //
    //             return newInitialValues;
    //         });
    //     }
    // }, [currentUser, clientSignatureDetails, formikRef]);

    // const mapFormDataToInitialValues = (data: any) => ({
    //     height: {
    //         value: data?.height?.value || "",
    //         unit: data?.height?.unit || "ft-in",
    //     },
    //     weight: {
    //         value: data?.weight?.value || "",
    //         unit: data?.weight?.unit || "kg",
    //     },
    //     current_activity_level: {
    //         frequency: data?.current_activity_level?.frequency || "",
    //         activities: data?.current_activity_level?.activities || [],
    //         intensity: data?.current_activity_level?.intensity || "",
    //         heart_rate_impact: data?.current_activity_level?.heart_rate_impact || "",
    //         exercise_capacity: data?.current_activity_level?.exercise_capacity || "",
    //         additional_notes: data?.current_activity_level?.additional_notes || "",
    //     },
    //     fitness_goals: {
    //         goals: data?.fitness_goals?.goals || "",
    //         past_exercise_experience: data?.fitness_goals?.past_exercise_experience || "",
    //         past_difficulty: {
    //             value: data?.fitness_goals?.past_difficulty?.value ?? "",
    //             text: data?.fitness_goals?.past_difficulty?.text || "",
    //         },
    //         trained_with_coach: {
    //             value: data?.fitness_goals?.trained_with_coach?.value ?? "",
    //             text: data?.fitness_goals?.trained_with_coach?.text || "",
    //         },
    //     },
    //     health_and_fitness: {
    //         under_medical_treatment: {
    //             value: data?.health_and_fitness?.under_medical_treatment?.value ?? "",
    //             text: data?.health_and_fitness?.under_medical_treatment?.text || "",
    //         },
    //         medical_conditions: {
    //             value: data?.health_and_fitness?.medical_conditions?.value ?? "",
    //             text: data?.health_and_fitness?.medical_conditions?.text || "",
    //         },
    //         doctor_advised_against_exercise: {
    //             value: data?.health_and_fitness?.doctor_advised_against_exercise?.value ?? "",
    //             text: data?.health_and_fitness?.doctor_advised_against_exercise?.text || "",
    //         },
    //     },
    //     additional_comments: data?.additional_comments || "",
    //     consent: {
    //         // agreed_to_terms: data?.consent?.agreed_to_terms,  // Null or boolean indicating agreement
    //         authorized_representative: data?.consent?.authorized_representative ?? false, // Boolean flag
    //         client_name: data?.consent?.client_name || "",  // Name of the client
    //         date: data?.consent?.date || "",  // Consent date
    //         // signature: data?.consent?.signature || "", // Base64 string for the signature
    //         agreed_to_terms: clientSignatureDetails.agreed_to_terms,
    //         representative_name: clientSignatureDetails.representative_name,
    //         is_authorized_representative: clientSignatureDetails.is_authorized_representative,
    //         signature: clientSignatureDetails.signature
    //     },
    // });


    const mapFormDataToInitialValues = useCallback((data: any) => ({
        height: {
            value: data?.height?.value || "",
            unit: data?.height?.unit || "ft-in",
        },
        weight: {
            value: data?.weight?.value || "",
            unit: data?.weight?.unit || "kg",
        },
        current_activity_level: {
            frequency: data?.current_activity_level?.frequency || "",
            activities: data?.current_activity_level?.activities || [],
            intensity: data?.current_activity_level?.intensity || "",
            heart_rate_impact: data?.current_activity_level?.heart_rate_impact || "",
            exercise_capacity: data?.current_activity_level?.exercise_capacity || "",
            additional_notes: data?.current_activity_level?.additional_notes || "",
        },
        fitness_goals: {
            goals: data?.fitness_goals?.goals || "",
            past_exercise_experience: data?.fitness_goals?.past_exercise_experience || "",
            past_difficulty: {
                value: data?.fitness_goals?.past_difficulty?.value ?? "",
                text: data?.fitness_goals?.past_difficulty?.text || "",
            },
            trained_with_coach: {
                value: data?.fitness_goals?.trained_with_coach?.value ?? "",
                text: data?.fitness_goals?.trained_with_coach?.text || "",
            },
        },
        health_and_fitness: {
            under_medical_treatment: {
                value: data?.health_and_fitness?.under_medical_treatment?.value ?? "",
                text: data?.health_and_fitness?.under_medical_treatment?.text || "",
            },
            medical_conditions: {
                value: data?.health_and_fitness?.medical_conditions?.value ?? "",
                text: data?.health_and_fitness?.medical_conditions?.text || "",
            },
            doctor_advised_against_exercise: {
                value: data?.health_and_fitness?.doctor_advised_against_exercise?.value ?? "",
                text: data?.health_and_fitness?.doctor_advised_against_exercise?.text || "",
            },
        },
        additional_comments: data?.additional_comments || "",
        consent: {
            authorized_representative: data?.consent?.authorized_representative ?? false,
            date: data?.consent?.date || "",
            agreed_to_terms: data?.consent?.agreed_to_terms,
            representative_name: clientSignatureDetails?.representative_name,
            is_authorized_representative: clientSignatureDetails?.is_authorized_representative,
            signature: clientSignatureDetails?.signature,
            client_name: currentUser?.first_name + ' ' + currentUser?.last_name,
        },
    }), [clientSignatureDetails, currentUser]);


    const onSubmit = useCallback((values: any, {setErrors}: FormikHelpers<any>) => {
        setIsLoading(true);
        try {
            const payload = {
                ...values,
                appointment_id: appointmentId ? appointmentId : currentUser?.appointment_id,
            }
            CommonService._client.ClientPhysicalActivityQuestionnaireDetailsSaveAPICall(payload)
                .then((response: IAPIResponseType<any>) => {
                    CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                    mapFormDataToInitialValues(payload);
                    setIsLoading(false);
                    onNext && onNext();
                }).catch((error: any) => {
                CommonService.handleErrors(setErrors, error, true);
                setIsLoading(false);
            })
        } catch (error) {
            CommonService.handleErrors(setErrors, error, true);
            setIsLoading(false);
        }
    }, [currentUser, onNext, mapFormDataToInitialValues, appointmentId]);

    const getPhysicalActivityQuestionnaireData = useCallback(() => {
        if (currentUser?.appointment_id) {
            CommonService._client.getPhysicalActivityQuestionnaire(currentUser?.appointment_id)
                .then((response: any) => {
                    setGetPhysicalActivityActivityData(response.data);
                    if (response?.data) {
                        const mappedValues = mapFormDataToInitialValues(response.data);
                        setInitialValues(mappedValues);
                    }
                }).catch((error: any) => {
                setGetPhysicalActivityActivityData([]);
            })
        }
    }, [currentUser?.appointment_id, mapFormDataToInitialValues]);

    useEffect(() => {
        getPhysicalActivityQuestionnaireData();
    }, [getPhysicalActivityQuestionnaireData]);

    useEffect(() => {
        if (clientSignatureDetails) {
            setInitialValues((prevState: any) => {
                return {
                    ...prevState,
                    consent: {
                        //   authorized_representative: clientSignatureDetails?.consent?.authorized_representative ?? false,
                        date: clientSignatureDetails?.date || "",
                        agreed_to_terms: clientSignatureDetails?.is_terms_acknowledged,
                        representative_name: clientSignatureDetails?.representative_name,
                        is_authorized_representative: clientSignatureDetails?.is_authorized_representative,
                        signature: clientSignatureDetails?.signature,
                        client_name: currentUser?.first_name + ' ' + currentUser?.last_name,
                    },

                }
            })
        }
    }, [getPhysicalActivityActivityData, currentUser, clientSignatureDetails]);


    const handleActivitiesOptionSelection = useCallback((optionId: string, selectedOptions: string[]) => {
        const activityOptions = _.cloneDeep(selectedOptions);
        if (optionId === "67e3aa9d3362dd8737c191bb") {
            activityOptions.length = 0; // Clear the array
            activityOptions.push(optionId); // Push the specific ID
            return activityOptions;
        } else {
            const specialIndex = activityOptions.findIndex(value => value === "67e3aa9d3362dd8737c191bb");
            if (specialIndex > -1) {
                activityOptions.splice(specialIndex, 1);
            }
            const index = activityOptions?.findIndex((value: string) => value === optionId);
            if (index > -1) {
                activityOptions.splice(index, 1);
            } else {
                activityOptions.push(optionId);
            }
            return activityOptions;
        }
    }, []);

    const handleSignaturePopupClose = useCallback(() => {
        setIsSignaturePopupOpen(false);
    }, []);


    return (
        <div
            className={"client_physical-activity-questionnaire client-registration-details-block"}>
            <div className="client-registration-details-block__header">
                <div className="client-registration-details-block__title" onClick={() => {
                    console.log(clientSignatureDetails)
                }}>
                    Physical Activity Questionnaire
                </div>
            </div>
            <div
                className={'justified-text mrg-bottom-25 client-registration-physical-activity-questionnaire-des mrg-right-70'}>
                Please take a moment to answer the following questions. Your responses will help us create a
                personalized plan to support your health and fitness journey.
            </div>
            <div className={'client-registration-details-block__body'}>
                <Formik
                    initialValues={initialValues}
                    validateOnChange={false}
                    validationSchema={validationSchema}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    innerRef={formikRef}
                    validateOnMount={true}
                    onSubmit={onSubmit}
                >
                    {({
                          values,
                          setFieldTouched,
                          validateForm,
                          touched,
                          errors,
                          isValid,
                          isSubmitting,
                          setFieldValue
                      }) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);

                        return (
                            <Form className="t-form" noValidate={true}>
                                <div className={'t-form-controls'}>
                                    <div className="ts-row">

                                        {/*Height Insertion */}
                                        <div className={'ts-col-md-4'}>
                                            <Field
                                                name={'height.value'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={'full_name_input_height'}
                                                            label={'Height'}
                                                            placeholder={'Enter Height'}
                                                            type={"text"}
                                                            required={true}
                                                            titleCase={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className={'ts-col-md-2 mrg-top-20'}>
                                            <Field
                                                name={'height.unit'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            id={'relationship_dd'}
                                                            options={heightUnitsList}
                                                            label={" "}
                                                            placeholder={'Select Relationship'}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            isClearable={false}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        {/*Height Insertion End*/}

                                        {/*Weight Insertion */}
                                        <div className={'ts-col-md-4'}>
                                            <Field
                                                name={'weight.value'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={'full_name_input'}
                                                            label={'Weight'}
                                                            placeholder={'Enter Weight'}
                                                            validationPattern={Patterns.NON_NEGATIVE_NUMBERS}
                                                            required={true}
                                                            titleCase={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className={'ts-col-md-2 mrg-top-20'}>
                                            <Field
                                                name={'weight.unit'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            id={'relationship_dd'}
                                                            options={weightUnitsList}
                                                            label={" "}
                                                            placeholder={'Select Relationship'}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            isClearable={false}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        {/*Weight Insertion End*/}


                                        <HorizontalLineComponent/>


                                        <div className={'pdd-left-10'}>
                                            {/* Current Activity Level:*/}
                                            <div className="form-header">
                                                Current Activity Level:
                                            </div>
                                            <div className="ts-row">
                                                <div className={'mrg-left-10 form-labeling-header mrg-bottom-15'}>
                                                    How often do you engage in physical activity each week?*
                                                </div>
                                                <div className={'ts-col-md-6'}>
                                                    <Field
                                                        name={'current_activity_level.frequency'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikSelectDropdownComponent
                                                                    id={'full_name_input_height'}
                                                                    label={''}
                                                                    placeholder={'Select Option'}
                                                                    options={physicalActivityFrequencyList}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="ts-row">
                                                <div className={'mrg-left-10 form-labeling-header mrg-bottom-15'}>
                                                    What types of physical activities do you participate in regularly?*
                                                    (Select all that apply)
                                                    {
                                                        (_.get(touched, "current_activity_level.activities") && !!(_.get(errors, "current_activity_level.activities"))) &&
                                                        <ErrorComponent
                                                            errorText={(_.get(errors, "current_activity_level.activities"))}/>
                                                    }
                                                </div>
                                            </div>
                                            <div className={'ts-row'}>
                                                <div className={'ts-col-lg-6'}>
                                                    {
                                                        column1?.map((option: any) => {
                                                            return <div key={option._id}>
                                                                <Field
                                                                    name={"current_activity_level.activities"}>
                                                                    {(field: FieldProps) => (
                                                                        <CheckBoxComponent
                                                                            id={'cb_' + option?.title + "_activities"}
                                                                            label={option?.title}
                                                                            checked={field.field?.value?.indexOf(option._id) > -1}
                                                                            onChange={() => {
                                                                                setFieldTouched(field.field?.name);
                                                                                setFieldValue(field.field?.name, handleActivitiesOptionSelection(option._id, field.field?.value));
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                        })
                                                    }
                                                </div>
                                                <div className={'ts-col-lg-6'}>
                                                    {
                                                        column2?.map((option: any) => {
                                                            return <div key={option._id}>
                                                                <Field
                                                                    name={"current_activity_level.activities"}>
                                                                    {(field: FieldProps) => (
                                                                        <CheckBoxComponent
                                                                            id={'cb_' + option?.title + "_activities"}
                                                                            label={option?.title}
                                                                            checked={field.field?.value?.indexOf(option._id) > -1}
                                                                            onChange={() => {
                                                                                setFieldTouched(field.field?.name);
                                                                                setFieldValue(field.field?.name, handleActivitiesOptionSelection(option._id, field.field?.value));
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                        })
                                                    }
                                                </div>
                                            </div>

                                            {values?.current_activity_level?.activities?.includes("67e3aa9d3362dd8737c191c6") &&
                                                <Field name={'current_activity_level.additional_notes'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikTextAreaComponent
                                                                rows={6}
                                                                id={'allergies_input_additional_notes'}
                                                                formikField={field}
                                                                label={''}
                                                                required={true}
                                                                fullWidth={true}
                                                                placeholder={'Enter your comments here'}/>
                                                        )
                                                    }
                                                </Field>
                                            }
                                            {/* Current Activity Level End*/}
                                            <div className="ts-row">
                                                <div className={'mrg-left-10 form-labeling-header mrg-bottom-15'}>
                                                    How would you describe the intensity of your usual physical
                                                    activity?*
                                                </div>
                                                <div className={'ts-col-md-6'}>
                                                    <Field
                                                        name={'current_activity_level.intensity'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikSelectDropdownComponent
                                                                    id={'full_name_input_height_intensity'}
                                                                    label={''}
                                                                    placeholder={'Select Option'}
                                                                    options={physicalActivityIntensityList}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="ts-row">
                                                <div className={'mrg-left-10 form-labeling-header mrg-bottom-15'}>
                                                    How often do you engage in activities that elevate your heart rate
                                                    or make
                                                    you sweat?*
                                                </div>
                                                <div className={'ts-col-md-6'}>
                                                    <Field
                                                        name={'current_activity_level.heart_rate_impact'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikSelectDropdownComponent
                                                                    id={'full_name_input_height_heart_rate_impact'}
                                                                    label={''}
                                                                    placeholder={'Select Option'}
                                                                    options={physicalActivityFrequencyForHeartRateImpactList}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="ts-row">
                                                <div className={'mrg-left-10 form-labeling-header mrg-bottom-15'}>
                                                    How would you describe your current capacity to exercise?*
                                                </div>
                                                <div className={'ts-col-md-6'}>
                                                    <Field
                                                        name={'current_activity_level.exercise_capacity'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikSelectDropdownComponent
                                                                    id={'full_name_input_height_exercise_capacity'}
                                                                    label={''}
                                                                    placeholder={'Select Option'}
                                                                    options={physicalActivityExerciseCapacityList}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                        </div>


                                        <HorizontalLineComponent/>
                                        <div className={'pdd-left-10'}>
                                            {/* Current Activity Level:*/}
                                            <div className="form-header">
                                                Fitness Goals:
                                            </div>

                                            <Field name={'fitness_goals.goals'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikTextAreaComponent
                                                            rows={6}
                                                            id={'allergies_input'}
                                                            formikField={field}
                                                            label={'What are your personal fitness/wellness goals? (ie. weight loss, strength, power, muscular endurance, cardio fitness, agility, flexibility, core stability, balance,\n' +
                                                                'self-confidence)'}
                                                            required={true}
                                                            fullWidth={true}
                                                            placeholder={'Enter your comments here'}/>
                                                    )
                                                }
                                            </Field>


                                            <div className="ts-row">
                                                <div className="ts-col-md-9 pdd-bottom-10 form-labeling-header">
                                                    Have you had any difficulty with physical activity in the past?*
                                                </div>
                                                <div className="ts-col-md-3 d-flex justify-content-end">
                                                    <Field name={`fitness_goals.past_difficulty.value`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikRadioButtonGroupComponent
                                                                    options={CommonService._staticData.yesNoOptions}
                                                                    displayWith={(option) => option.title}
                                                                    valueExtractor={(option) => option.code}
                                                                    required={true}
                                                                    formikField={field}
                                                                    id={"difficulty_physical"}
                                                                    onChange={(value) => {
                                                                        setFieldValue(`fitness_goals.past_difficulty.text`, "");
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            {
                                                values?.fitness_goals?.past_difficulty?.value &&
                                                <div className="ts-row"
                                                     key={"comment_row_physical"}>
                                                    <div className={"ts-col-md-12"}>
                                                        <Field name={`fitness_goals.past_difficulty.text`}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikTextAreaComponent
                                                                        id={"input_physical"}
                                                                        placeholder={"Enter your comments here"}
                                                                        disabled={!values?.fitness_goals?.past_difficulty?.value}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        rows={"5"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                            }
                                            {/* Current Activity Level End*/}


                                            <div className="ts-row">
                                                <div className="ts-col-md-8 pdd-bottom-10 form-labeling-header">
                                                    Have you ever worked with a personal trainer or taken structured
                                                    fitness classes?*
                                                </div>
                                                <div className="ts-col-md-4 d-flex justify-content-end">
                                                    <Field name={`fitness_goals.trained_with_coach.value`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikRadioButtonGroupComponent
                                                                    options={CommonService._staticData.yesNoOptions}
                                                                    displayWith={(option) => option.title}
                                                                    valueExtractor={(option) => option.code}
                                                                    required={true}
                                                                    formikField={field}
                                                                    id={"difficulty_physical"}
                                                                    onChange={(value) => {
                                                                        setFieldValue(`fitness_goals.trained_with_coach.text`, "");
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            {
                                                values?.fitness_goals?.trained_with_coach?.value &&
                                                <div className="ts-row"
                                                     key={"comment_row_physical_trained"}>
                                                    <div className={"ts-col-md-12"}>
                                                        <Field name={`fitness_goals.trained_with_coach.text`}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikTextAreaComponent
                                                                        id={"input_physical"}
                                                                        placeholder={"Enter your comments here"}
                                                                        disabled={!values?.fitness_goals?.trained_with_coach?.value}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        rows={"5"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                            }

                                            <Field name={'fitness_goals.past_exercise_experience'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikTextAreaComponent
                                                            rows={6}
                                                            id={'allergies_input'}
                                                            formikField={field}
                                                            label={'Please describe your current and/or past exercise experience:'}
                                                            required={true}
                                                            fullWidth={true}
                                                            placeholder={'Enter your comments here'}/>
                                                    )
                                                }
                                            </Field>

                                            {/* Current Activity Level End*/}

                                        </div>
                                        <HorizontalLineComponent/>

                                        <div className={'pdd-left-10'}>
                                            {/* Current Activity Level:*/}
                                            <div className="form-header">
                                                Current Health Information:
                                            </div>


                                            <div className="ts-row">
                                                <div className="ts-col-md-9 pdd-bottom-10 form-labeling-header">
                                                    Are you currently being treated for any medical conditions
                                                    or injuries?*
                                                </div>
                                                <div className="ts-col-md-3 d-flex justify-content-end">
                                                    <Field name={`health_and_fitness.under_medical_treatment.value`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikRadioButtonGroupComponent
                                                                    options={CommonService._staticData.yesNoOptions}
                                                                    displayWith={(option) => option.title}
                                                                    valueExtractor={(option) => option.code}
                                                                    required={true}
                                                                    formikField={field}
                                                                    id={"difficulty_physical"}
                                                                    onChange={(value) => {
                                                                        setFieldValue(`health_and_fitness.under_medical_treatment.text`, "");
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            {
                                                values?.health_and_fitness?.under_medical_treatment.value &&
                                                <div className="ts-row"
                                                     key={"comment_row_physical"}>
                                                    <div className={"ts-col-md-12"}>
                                                        <Field name={`health_and_fitness.under_medical_treatment.text`}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikTextAreaComponent
                                                                        id={"input_physical"}
                                                                        placeholder={"Enter your comments here"}
                                                                        disabled={!values?.health_and_fitness?.under_medical_treatment?.value}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        rows={"5"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                            }


                                            <div className="ts-row">
                                                <div className="ts-col-md-9 pdd-bottom-10 form-labeling-header">
                                                    Are there any medical conditions or injuries (past or present) that
                                                    could be affected or get worse with exercise?*
                                                </div>
                                                <div className="ts-col-md-3 d-flex justify-content-end">
                                                    <Field name={`health_and_fitness.medical_conditions.value`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikRadioButtonGroupComponent
                                                                    options={CommonService._staticData.yesNoOptions}
                                                                    displayWith={(option) => option.title}
                                                                    valueExtractor={(option) => option.code}
                                                                    required={true}
                                                                    formikField={field}
                                                                    id={"difficulty_physical"}
                                                                    onChange={(value) => {
                                                                        setFieldValue(`health_and_fitness.medical_conditions.text`, "");
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            {
                                                values?.health_and_fitness?.medical_conditions.value &&
                                                <div className="ts-row"
                                                     key={"comment_row_physical"}>
                                                    <div className={"ts-col-md-12"}>
                                                        <Field name={`health_and_fitness.medical_conditions.text`}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikTextAreaComponent
                                                                        id={"input_physical"}
                                                                        placeholder={"Enter your comments here"}
                                                                        disabled={!values?.health_and_fitness?.medical_conditions?.value}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        rows={"5"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                            }


                                            <div className="ts-row">
                                                <div className="ts-col-md-9 pdd-bottom-10 form-labeling-header">
                                                    Have you ever been advised by a physician not to exercise or
                                                    participate in physical activities?*
                                                </div>
                                                <div className="ts-col-md-3 d-flex justify-content-end">
                                                    <Field
                                                        name={`health_and_fitness.doctor_advised_against_exercise.value`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikRadioButtonGroupComponent
                                                                    options={CommonService._staticData.yesNoOptions}
                                                                    displayWith={(option) => option.title}
                                                                    valueExtractor={(option) => option.code}
                                                                    required={true}
                                                                    formikField={field}
                                                                    id={"difficulty_physical"}
                                                                    onChange={(value) => {
                                                                        setFieldValue(`health_and_fitness.doctor_advised_against_exercise.text`, "");
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            {
                                                values?.health_and_fitness?.doctor_advised_against_exercise.value &&
                                                <div className="ts-row"
                                                     key={"comment_row_physical"}>
                                                    <div className={"ts-col-md-12"}>
                                                        <Field
                                                            name={`health_and_fitness.doctor_advised_against_exercise.text`}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikTextAreaComponent
                                                                        id={"input_physical"}
                                                                        placeholder={"Enter your comments here"}
                                                                        disabled={!values?.health_and_fitness?.doctor_advised_against_exercise?.value}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        rows={"5"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                            }

                                            <HorizontalLineComponent/>

                                            <div className="form-header">
                                                Additional Comments:
                                            </div>

                                            <Field name={'additional_comments'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikTextAreaComponent
                                                            rows={6}
                                                            id={'allergies_input'}
                                                            formikField={field}
                                                            label={'Is there anything else you would like us to know about your fitness level, goals,\n' +
                                                                'or preferences?'}
                                                            fullWidth={true}
                                                            placeholder={'Enter your comments here'}/>
                                                    )
                                                }
                                            </Field>

                                            <HorizontalLineComponent/>
                                            <div className="form-header">
                                                Certification, Acknowledgment of Risks, and Consent to Participate:
                                            </div>

                                            <div>
                                                <div className={'justified-text mrg-bottom-20 consent-text'}>
                                                    By completing this questionnaire, I certify that the information I
                                                    have provided is true, accurate, current, and complete to the best
                                                    of my knowledge.
                                                </div>
                                                <div className={'justified-text mrg-bottom-20 consent-text'}>
                                                    I acknowledge that participating in physical activities, including
                                                    but not limited to exercise routines, strength training, and other
                                                    fitness activities, can be strenuous and carries inherent risks,
                                                    such as the possibility of injury, cardiovascular events, or other
                                                    health complications. These risks may be increased if I have any
                                                    pre-existing medical conditions, physical limitations, or if I am
                                                    not accustomed to regular physical activity.
                                                </div>
                                                <div className={'justified-text mrg-bottom-20 consent-text'}>
                                                    I understand and agree that it is my sole responsibility to consult
                                                    with a physician or other qualified healthcare provider before using
                                                    any exercise equipment or engaging in physical activities. I confirm
                                                    that I have disclosed all relevant injuries or health conditions to
                                                    the Performance Center staff and am aware that it is strongly
                                                    recommended that I seek medical advice before beginning or modifying
                                                    any exercise program.
                                                </div>
                                                <div className={'justified-text mrg-bottom-20 consent-text'}>
                                                    I voluntarily agree to participate in these activities and assume
                                                    all risks associated with my participation. I further understand
                                                    that the Performance Center staff will take all necessary
                                                    precautions to ensure my safety, but the ultimate responsibility for
                                                    my health and safety rests with me.
                                                </div>
                                                <div className={'justified-text mrg-bottom-20 consent-text'}>
                                                    In the event of a medical emergency, I authorize the staff at
                                                    Kinergy Sports Medicine and Performance to perform or seek emergency
                                                    medical treatment if necessary. I also agree to promptly inform the
                                                    staff of any changes in my health or medical status that may affect
                                                    my ability to safely participate in these physical activities.
                                                </div>

                                                <div className="ts-row">
                                                    <div className="ts-col-12">
                                                        <Field name={'consent.agreed_to_terms'}
                                                               className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikCheckBoxComponent
                                                                        label={<>I acknowledge that I have read and
                                                                            agree to the
                                                                            statement as written above.</>}
                                                                        required={true}
                                                                        formikField={field}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className="ts-row">
                                                    <div className="ts-col-12">
                                                        <Field name={'consent.is_authorized_representative'}
                                                               className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikCheckBoxComponent
                                                                        label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                                        formikField={field}
                                                                        onChange={() => {
                                                                            setFieldValue('consent.representative_name', '');
                                                                        }}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className="ts-row">
                                                    <div className="ts-col-md-12 ts-col-lg-6">
                                                        <Field name={'consent.client_name'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"name_input"}
                                                                        label={'Client Name'}
                                                                        placeholder={'Enter your name'}
                                                                        required={true}
                                                                        disabled={true}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        titleCase={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className="ts-col-md-12 ts-col-lg-6">
                                                        <Field name={'consent.date'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikDatePickerComponent
                                                                        id={"dob_input"}
                                                                        label={'Date'}
                                                                        placeholder={'Enter Date'}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        disabled={true}
                                                                        readOnly={true}
                                                                        // clearable={false}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>

                                                {
                                                    values?.consent?.is_authorized_representative &&
                                                    <div className="ts-row">
                                                        <div className="ts-col-12">
                                                            <Field name={'consent.representative_name'}
                                                                   className="t-form-control">
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikInputComponent
                                                                            label={'Authorized Representative Name'}
                                                                            placeholder={'Enter Representative Name'}
                                                                            required={true}
                                                                            formikField={field}
                                                                            fullWidth={true}
                                                                            titleCase={true}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>
                                                        </div>
                                                    </div>
                                                }
                                                {!(values?.consent?.signature && initialValues?.consent?.signature) &&
                                                    <div className="ts-row">
                                                        <div className="ts-col-12">
                                                            <FormControlLabelComponent
                                                                className={"font-weight-thin"}
                                                                label={"Signature of Client or Authorized Representative"}
                                                                required={true}/>
                                                            <ButtonComponent
                                                                className={'add-signature-button'}
                                                                onClick={() => {
                                                                    setIsSignaturePopupOpen(true);
                                                                }}
                                                                prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                                variant={'link'}
                                                                color={'info'}
                                                                size={'medium'}
                                                            >
                                                                Add Signature
                                                            </ButtonComponent>
                                                        </div>
                                                        <div className={'mrg-left-10'}>
                                                            {
                                                                (_.get(touched, "consent.signature") && !!(_.get(errors, "consent.signature"))) &&
                                                                <ErrorComponent
                                                                    errorText={(_.get(errors, "consent.signature"))}/>
                                                            }
                                                        </div>
                                                    </div>
                                                }
                                                {(values?.consent?.signature && initialValues?.consent?.signature) &&
                                                    <>
                                                        <ESignApprovalComponent isSigned={true}
                                                                                signature_url={values?.consent?.signature}
                                                        />
                                                        <div className={'signature-remove-button'}>
                                                            <LinkComponent onClick={() => {
                                                                setFieldValue('consent.signature', "");
                                                            }
                                                            }>
                                                                Remove Signature
                                                            </LinkComponent>
                                                        </div>
                                                    </>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div className="t-form-actions">
                                        <ButtonComponent
                                            id={"next_btn"}
                                            type={"submit"}
                                            size={'large'}
                                            isLoading={isLoading}
                                            disabled={!isValid || isLoading}
                                            suffixIcon={<ImageConfig.ArrowRightCircle/>}
                                        >
                                            Next
                                        </ButtonComponent>
                                    </div>
                                </div>
                            </Form>
                        )

                    }}
                </Formik>
                <ModalComponent isOpen={isSignaturePopupOpen}
                                onClose={handleSignaturePopupClose}
                                size={"xl"}
                >
                    <SignaturePopupComponent
                        onSign={(signImage: any) => {
                            formikRef?.current.setFieldValue('consent.signature', signImage);
                            setInitialValues({
                                ...formikRef?.current?.values,
                                consent: {
                                    ...formikRef?.current?.values.consent,
                                    signature: signImage
                                }
                            });
                            handleSignaturePopupClose();
                        }}
                        handleSignaturePopupClose={handleSignaturePopupClose}
                    />
                </ModalComponent>
            </div>
        </div>
    )
}

export default ClientRegistrationPhysicalActivityQuestionnaireComponent;
